import {
	PricingMode,
	TransportConfig,
	TransportConfigRequest,
} from '../../../services/transport-config.services';

export const NUMBER_MAX_DECIMALS = /^(?:\d+|\d*\.\d{1,10})$/;
export const PRICING_MODES: PricingMode[] = ['DAILY_RATE', 'PER_TON_KM'];
export const CATEGORY_NAMES = ['category1', 'category2', 'category3'] as const;
export type CategoryName = typeof CATEGORY_NAMES[number];

/** Seed shown only if the API returns nothing (spec §2.4 values). */
export const DEFAULT_ZONES: {
	id: string;
	from: number;
	to: number | null;
	mode: PricingMode;
}[] = [
	{ id: '0.1', from: 0, to: 10, mode: 'DAILY_RATE' },
	{ id: '0.2', from: 10.1, to: 20, mode: 'DAILY_RATE' },
	{ id: '0.3', from: 20.1, to: 30, mode: 'DAILY_RATE' },
	{ id: '0.4', from: 30.1, to: 40, mode: 'DAILY_RATE' },
	{ id: '0.5', from: 40.1, to: 50, mode: 'DAILY_RATE' },
	{ id: '1', from: 50.1, to: 60, mode: 'PER_TON_KM' },
	{ id: '2', from: 60.1, to: 70, mode: 'PER_TON_KM' },
	{ id: '3', from: 70.1, to: 89.9, mode: 'PER_TON_KM' },
	{ id: '4', from: 90, to: 119.9, mode: 'PER_TON_KM' },
	{ id: '5', from: 120, to: null, mode: 'PER_TON_KM' },
];

export interface FormValues {
	margin: string;
	workingHoursPerDay: string;
	averageSpeedKmh: string;
	categories: Record<CategoryName, { from: string; to: string }>;
	zones: {
		ids: string[];
		distances: { from: string; to: string }[];
		modes: (PricingMode | '')[];
		/** [category row][zone column] */
		plnValues: string[][];
	};
}

const toStr = (value: unknown) =>
	value === null || value === undefined ? '' : String(value);

export const createInitialValues = (
	config: TransportConfig | undefined,
): FormValues => {
	if (!config) {
		return {
			margin: '5',
			workingHoursPerDay: '8',
			averageSpeedKmh: '45',
			categories: {
				category1: { from: '0', to: '10' },
				category2: { from: '10.1', to: '19' },
				category3: { from: '19.1', to: '27' },
			},
			zones: {
				ids: DEFAULT_ZONES.map((z) => z.id),
				distances: DEFAULT_ZONES.map((z) => ({
					from: toStr(z.from),
					to: toStr(z.to),
				})),
				modes: DEFAULT_ZONES.map((z) => z.mode),
				plnValues: CATEGORY_NAMES.map(() =>
					DEFAULT_ZONES.map((z) => (z.mode === 'DAILY_RATE' ? '1900' : '0.3')),
				),
			},
		};
	}

	const zoneEntries = Object.entries(config.zones || {}).sort(
		([, a], [, b]) => Number(a.from) - Number(b.from),
	);
	const categoryKeys = Object.entries(config.categories || {})
		.sort(([, a], [, b]) => Number(a.from) - Number(b.from))
		.map(([key]) => key)
		.slice(0, CATEGORY_NAMES.length);

	const categories = {} as FormValues['categories'];
	CATEGORY_NAMES.forEach((name, idx) => {
		const category = config.categories?.[categoryKeys[idx]];
		categories[name] = {
			from: toStr(category?.from),
			to: toStr(category?.to),
		};
	});

	return {
		margin: toStr(config.margin),
		workingHoursPerDay: toStr(config.workingHoursPerDay ?? 8),
		averageSpeedKmh: toStr(config.averageSpeedKmh ?? 45),
		categories,
		zones: {
			ids: zoneEntries.map(([id]) => id),
			distances: zoneEntries.map(([, zone]) => ({
				from: toStr(zone.from),
				to: toStr(zone.to),
			})),
			modes: zoneEntries.map(([, zone]) =>
				PRICING_MODES.includes(zone.pricingMode as PricingMode)
					? (zone.pricingMode as PricingMode)
					: '',
			),
			plnValues: CATEGORY_NAMES.map((_, rowIdx) =>
				zoneEntries.map(([zoneId]) =>
					toStr(config.priceMatrix?.[categoryKeys[rowIdx]]?.value?.[zoneId]),
				),
			),
		},
	};
};

export const buildTransportConfigPayload = (
	values: FormValues,
	countryCode: 'PL' | 'MD',
): TransportConfigRequest => {
	const num = (value: string) => {
		const parsed = parseFloat(value);
		return isNaN(parsed) ? 0 : parsed;
	};
	const ids = values.zones.ids;

	const zones: TransportConfigRequest['zones'] = {};
	ids.forEach((id, idx) => {
		const { from, to } = values.zones.distances[idx];
		zones[id] = {
			from: num(from),
			pricingMode: values.zones.modes[idx] as PricingMode,
		};
		if (to !== '') zones[id].to = num(to);
	});

	const categories: TransportConfigRequest['categories'] = {};
	const priceMatrix: TransportConfigRequest['priceMatrix'] = {};
	CATEGORY_NAMES.forEach((name, rowIdx) => {
		const key = String(rowIdx + 1);
		categories[key] = {
			from: num(values.categories[name].from),
			to: num(values.categories[name].to),
		};
		const value: Record<string, number> = {};
		ids.forEach((id, colIdx) => {
			value[id] = num(values.zones.plnValues[rowIdx]?.[colIdx]);
		});
		priceMatrix[key] = { value };
	});

	return {
		countryCode,
		margin: num(values.margin),
		workingHoursPerDay: num(values.workingHoursPerDay),
		averageSpeedKmh: num(values.averageSpeedKmh),
		categories,
		zones,
		priceMatrix,
	};
};
